/**
 * Entry Calendar Component
 *
 * Custom month calendar grid for the relationship tracker.
 * Each day cell shows a color-coded background based on the entry rating.
 * Clicking a day navigates to the full-page entry form at /tracker/entries/:date.
 */

import { useMemo, useState } from "react";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  eachDayOfInterval,
  isSameMonth,
  isToday,
  isFuture,
} from "date-fns";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useRelationshipEntries } from "@/hooks/useRelationshipEntries";
import { cn } from "@/lib/utils";
import { stripMarkdownToText } from "@/lib/markdown";
import {
  EVENT_FIELDS,
  type EventField,
  type RelationshipEntry,
} from "@/types/relationshipEntry";

/**
 * Color coding for event dots on calendar cells.
 * Grouped semantically so related events share a color family.
 */
const EVENT_DOT_STYLES: Record<EventField, string> = {
  had_meaningful_conversation: "bg-blue-500 dark:bg-blue-600",
  had_conflict: "bg-red-500 dark:bg-red-600",
  had_physical_affection: "bg-pink-400 dark:bg-pink-600",
  had_intimacy: "bg-pink-600 dark:bg-pink-700",
  had_quality_time: "bg-emerald-500 dark:bg-emerald-600",
  had_support: "bg-sky-500 dark:bg-sky-600",
  had_repair_attempt: "bg-amber-500 dark:bg-amber-600",
  spent_apart: "bg-muted-foreground",
};

function EventDots({ entry }: { entry: RelationshipEntry }) {
  const activeEvents = EVENT_FIELDS.filter((field) => entry[field]);
  if (activeEvents.length === 0) return null;
  return (
    <div className="flex gap-0.5 mt-1 flex-wrap justify-center max-w-full px-0.5">
      {activeEvents.map((field) => (
        <span
          key={field}
          className={cn("h-1.5 w-1.5 rounded-full", EVENT_DOT_STYLES[field])}
          title={field.replace("had_", "").replace(/_/g, " ")}
        />
      ))}
    </div>
  );
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function getRatingColor(rating: number): {
  bg: string;
  text: string;
  ring: string;
} {
  if (rating <= 2)
    return {
      bg: "bg-red-500/20 dark:bg-red-600/20",
      text: "text-red-700 dark:text-red-300",
      ring: "ring-red-500/30",
    };
  if (rating <= 4)
    return {
      bg: "bg-orange-500/20 dark:bg-orange-600/20",
      text: "text-orange-700 dark:text-orange-300",
      ring: "ring-orange-500/30",
    };
  if (rating <= 6)
    return {
      bg: "bg-yellow-500/20 dark:bg-yellow-600/20",
      text: "text-yellow-700 dark:text-yellow-300",
      ring: "ring-yellow-500/30",
    };
  if (rating <= 8)
    return {
      bg: "bg-emerald-500/20 dark:bg-emerald-600/20",
      text: "text-emerald-700 dark:text-emerald-300",
      ring: "ring-emerald-500/30",
    };
  return {
    bg: "bg-green-500/25 dark:bg-green-600/25",
    text: "text-green-700 dark:text-green-300",
    ring: "ring-green-500/30",
  };
}

export function EntryCalendar() {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const navigate = useNavigate();

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);

  const { data: entriesData } = useRelationshipEntries(
    format(monthStart, "yyyy-MM-dd"),
    format(monthEnd, "yyyy-MM-dd")
  );

  const entryMap = useMemo(() => {
    const map = new Map<string, RelationshipEntry>();
    if (entriesData?.entries) {
      for (const entry of entriesData.entries) {
        map.set(entry.entry_date, entry);
      }
    }
    return map;
  }, [entriesData]);

  // Build the full grid of days (including padding from prev/next months)
  const calendarDays = useMemo(() => {
    const start = startOfWeek(monthStart);
    const end = endOfWeek(monthEnd);
    return eachDayOfInterval({ start, end });
  }, [monthStart, monthEnd]);

  const handleDayClick = (date: Date) => {
    if (isFuture(date)) return;
    navigate(`/tracker/entries/${format(date, "yyyy-MM-dd")}`);
  };

  return (
    <div>
      {/* Month header */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-xl font-semibold tracking-tight">
          {format(currentMonth, "MMMM yyyy")}
        </h2>
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="text-xs h-8 px-3"
            onClick={() => setCurrentMonth(new Date())}
          >
            Today
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Weekday headers */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS.map((day) => (
          <div
            key={day}
            className="text-center text-xs font-medium text-muted-foreground py-2"
          >
            {day}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="grid grid-cols-7 gap-1">
        {calendarDays.map((day) => {
          const dateStr = format(day, "yyyy-MM-dd");
          const entry = entryMap.get(dateStr);
          const inMonth = isSameMonth(day, currentMonth);
          const today = isToday(day);
          const future = isFuture(day);
          const colors = entry ? getRatingColor(entry.rating) : null;

          return (
            <button
              key={dateStr}
              onClick={() => handleDayClick(day)}
              disabled={future || !inMonth}
              className={cn(
                "relative min-h-[80px] flex flex-col items-center justify-start pt-2 rounded-lg text-sm transition-all",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                !inMonth && "opacity-0 pointer-events-none",
                future &&
                  inMonth &&
                  "opacity-30 cursor-not-allowed hover:bg-transparent",
                !future && inMonth && !entry && "hover:bg-accent/50",
                today && !entry && "ring-2 ring-primary/40 font-semibold",
                entry &&
                  colors &&
                  `${colors.bg} ring-1 ${colors.ring} hover:opacity-80`
              )}
            >
              <span
                className={cn(
                  "text-sm leading-none",
                  !inMonth && "text-muted-foreground",
                  today && "font-bold",
                  entry && colors && colors.text
                )}
              >
                {day.getDate()}
              </span>
              {entry && (
                <>
                  <span
                    className={cn(
                      "text-[10px] font-bold leading-none mt-1",
                      colors && colors.text
                    )}
                  >
                    {entry.rating}
                  </span>
                  <EventDots entry={entry} />
                  {entry.notes && (
                    <span className="text-[9px] leading-tight mt-1 px-1 text-muted-foreground truncate max-w-full">
                      {stripMarkdownToText(entry.notes)}
                    </span>
                  )}
                </>
              )}
            </button>
          );
        })}
      </div>

      {/* Empty state hint */}
      {entryMap.size === 0 && !isFuture(monthStart) && (
        <p className="text-center text-xs text-muted-foreground mt-3">
          Tap any day to log how you felt about your relationship.
        </p>
      )}

      {/* Legend */}
      <div className="flex items-center justify-center gap-3 mt-4 text-[11px] text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-red-500/20 dark:bg-red-600/20 ring-1 ring-red-500/30" />
          <span>1-2</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-orange-500/20 dark:bg-orange-600/20 ring-1 ring-orange-500/30" />
          <span>3-4</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-yellow-500/20 dark:bg-yellow-600/20 ring-1 ring-yellow-500/30" />
          <span>5-6</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-emerald-500/20 dark:bg-emerald-600/20 ring-1 ring-emerald-500/30" />
          <span>7-8</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded bg-green-500/25 dark:bg-green-600/25 ring-1 ring-green-500/30" />
          <span>9-10</span>
        </div>
      </div>
    </div>
  );
}
